/* global BigInt */
// BigInt is ES2020; CRA's "react-app" ESLint env only declares ES2015 globals, so it
// has to be declared here for the dev-server lint pass to accept it.
import { executePerps, queryContract, queryBalance } from "../../../Components/functions/Contract";
import { toast } from "react-toastify";
import { ethers } from "ethers";
import { useWalletConnection } from "../../../hooks/useWalletConnection";
import { makeWithdrawRequestEvm } from "../../../evm/services/vaultsEvmService";
import { fetchCombinedBalance } from "../../../evm/services/tradingEvmService";
import { getNibiruSDK, getContractAddresses, isTestnet, getCollateralDenom } from "../../../config/network";

const nibiru = getNibiruSDK();

// Helper function to get vault contract address based on network
const getVaultContractAddress = () => {
  const address = getContractAddresses().vault;
  if (!address) {
    throw new Error(`Vault contract address not configured for ${isTestnet() ? 'testnet' : 'mainnet'}`);
  }
  return address;
};
/**
 * Get user's ERC20 vault token balance (for EVM wallets)
 * @param {string} account - User's EVM address
 * @param {string} vaultTokenErc20Address - ERC20 address of vault token
 * @returns {Promise<string>} - Formatted balance
 */
export const getUserVaultTokenBalanceEvm = async (account, vaultTokenErc20Address) => {
  try {
    console.log("Starting ERC20 vault token balance query:", { account, vaultTokenErc20Address });
    
    if (!account || !vaultTokenErc20Address) {
      console.log("Missing required parameters for vault token balance query");
      return "0.00";
    }
    
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    
    // Get vault token balance directly using provider
    console.log("Executing ERC20 balanceOf call");
    const balance = await provider.call({
      to: vaultTokenErc20Address,
      data: ethers.concat([
        ethers.id("balanceOf(address)").slice(0, 10),
        ethers.AbiCoder.defaultAbiCoder().encode(["address"], [account])
      ])
    });

    const balanceDecoded = ethers.AbiCoder.defaultAbiCoder().decode(["uint256"], balance)[0];
    
    // Fix: Convert string to number for decimals
    const decimals = parseInt(process.env.REACT_APP_USDC_DECIMALS || "6", 10);
    const formattedBalance = Number(ethers.formatUnits(balanceDecoded, decimals));
    
    console.log("ERC20 Vault Token Balance result:", {
      account: account,
      contractAddress: vaultTokenErc20Address,
      rawBalance: balanceDecoded.toString(),
      decimals: decimals,
      formattedBalance: formattedBalance
    });

    return formattedBalance.toFixed(2);
  } catch (error) {
    console.error("Error fetching ERC20 vault token balance:", error);
    return "0.00";
  }
};

/**
 * Get user's USDC balance (for EVM wallets)
 * @param {string} account - User's EVM address
 * @returns {Promise<string>} - Formatted balance
 */
export const getUserUsdcBalanceEvm = async (account) => {
  try {
    if (!account) return "0.00";
    
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const usdcContractAddress = process.env.REACT_APP_NIBIRU_USDC_CONTRACT_ADDRESS;
    
    if (!usdcContractAddress) {
      console.error("USDC contract address not configured");
      return "0.00";
    }

    // Get USDC balance directly using provider
    const balance = await provider.call({
      to: usdcContractAddress,
      data: ethers.concat([
        ethers.id("balanceOf(address)").slice(0, 10),
        ethers.AbiCoder.defaultAbiCoder().encode(["address"], [account])
      ])
    });

    const balanceDecoded = ethers.AbiCoder.defaultAbiCoder().decode(["uint256"], balance)[0];
    const formattedBalance = Number(ethers.formatUnits(balanceDecoded, 6)); // USDC has 6 decimals
    
    console.log(`ERC20 USDC Balance for ${account}:`, {
      raw: balanceDecoded.toString(),
      formatted: formattedBalance,
      contractAddress: usdcContractAddress
    });

    return formattedBalance.toFixed(2);
  } catch (error) {
    console.error("Error fetching ERC20 USDC balance:", error);
    return "0.00";
  }
};

/**
 * Get user's vault deposits (shares balance) - updated to support EVM
 * @param {string} account - User's wallet address
 * @param {boolean} isEvm - Whether this is an EVM wallet
 * @returns {Promise<Array>} - List of user deposits
 */
export const getUserVaultDeposits = async (account, isEvm = false) => {
  try {
    if (!account) return [];

    const decimals = parseInt(process.env.REACT_APP_USDC_DECIMALS || "6", 10);
    const vaultAddress = getVaultContractAddress();

    // Resolve the share denom from the contract rather than trusting the env var,
    // which goes stale whenever the vault is redeployed.
    let cleanVaultShareDenom = process.env.REACT_APP_VAULT_TOKEN_DENOM || null;
    try {
      const vaultShareDenom = await queryContract(
        vaultAddress,
        { get_vault_share_denom: {} },
        nibiru.endptTm
      );
      cleanVaultShareDenom = String(vaultShareDenom).replace(/"/g, '');
    } catch (denomError) {
      console.warn("Falling back to env vault share denom:", denomError?.message);
    }

    if (!cleanVaultShareDenom) {
      console.error("No vault share denom available");
      return [];
    }

    // Vault shares can sit in EITHER of two places, and an EVM user can hold both:
    //
    //   1. the bank module, under the user's bech32 address — every Cosmos deposit,
    //      plus any EVM deposit whose bridge leg did not run (sendSharesToEvm false,
    //      or the SaiEvm before/after balance delta came out as 0), and anything
    //      returned by a cancelled withdraw request;
    //   2. the funtoken ERC20, under the user's 0x address — EVM deposits that were
    //      bridged across.
    //
    // Reading only one side is why a deposit that landed on-chain could still render
    // as "no deposits". Sum both; they are disjoint, so there is no double counting.
    let sharesBaseUnits = 0n;

    let bech32Address = isEvm ? null : account;
    if (isEvm) {
      try {
        const { evmToBech32Address } = await import("../../../evm/services/EvmContract");
        bech32Address = await evmToBech32Address(account);
      } catch (conversionError) {
        console.warn("EVM to bech32 conversion failed:", conversionError?.message);
      }
    }

    if (bech32Address) {
      try {
        const bankBalance = await queryBalance(
          bech32Address,
          cleanVaultShareDenom,
          nibiru.endptTm
        );
        if (bankBalance?.amount) {
          sharesBaseUnits += BigInt(bankBalance.amount);
        }
      } catch (bankError) {
        console.warn("Vault share bank balance query failed:", bankError?.message);
      }
    }

    if (isEvm) {
      try {
        const { getVaultShareErc20Address, getVaultShareBalanceBaseUnitsEvm } =
          await import("../../../evm/services/vaultsEvmService");
        const vaultShareErc20Address = await getVaultShareErc20Address(vaultAddress);
        const erc20Base = await getVaultShareBalanceBaseUnitsEvm(account, vaultShareErc20Address);
        sharesBaseUnits += BigInt(erc20Base || "0");
      } catch (erc20Error) {
        console.warn("Vault share ERC20 balance query failed:", erc20Error?.message);
      }
    }

    console.log("Vault share balance (base units):", {
      account,
      bech32Address,
      isEvm,
      denom: cleanVaultShareDenom,
      sharesBaseUnits: sharesBaseUnits.toString(),
    });

    if (sharesBaseUnits <= 0n) {
      return [];
    }

    const sharesAmount = Number(sharesBaseUnits) / Math.pow(10, decimals);

    // Get actual share price from contract for accurate current value
    let currentValue = sharesAmount;
    let sharePrice = 1.0;

    try {
      const snapshot = await queryContract(
        vaultAddress,
        { vault_snapshot: {} },
        nibiru.endptTm
      );

      if (snapshot && snapshot.share_price) {
        sharePrice = parseFloat(snapshot.share_price);
        currentValue = sharesAmount * sharePrice;
      }
    } catch (priceError) {
      console.warn("Could not get share price, using 1:1 ratio:", priceError);
    }

    return [{
      vaultName: "USDC Vault",
      depositAmount: currentValue.toFixed(2),         // Current value (original deposit unknown on-chain)
      vaultTokens: sharesAmount.toFixed(2),           // Current vault tokens held (display)
      vaultTokenName: "SLP-USDC",                     // Token name for display
      currentValue: currentValue.toFixed(2),          // Current USD value
      sharesAmount: sharesAmount.toFixed(6),          // Keep for compatibility
      sharesBaseUnits: sharesBaseUnits.toString(),    // Exact on-chain amount
      denom: cleanVaultShareDenom,                    // Actual denom
      sharePrice: sharePrice.toFixed(4),              // Current share price
    }];

  } catch (error) {
    console.error("Error fetching user vault deposits:", error);
    return [];
  }
};

/**
 * Get user's token balance - updated to support EVM
 * @param {string} account - User's wallet address
 * @param {string} denom - Token denomination to check
 * @param {boolean} isEvm - Whether this is an EVM wallet
 * @returns {Promise<string>} - Formatted balance
 */
export const getUserTokenBalance = async (account, denom, isEvm = false) => {
  try {
    if (!account) return "0.00";
    
    if (isEvm) {
      // For EVM wallets, query combined ERC20 + bank balance
      const bech32Addr = window.__nibiru_bech32 || null;
      const { total } = await fetchCombinedBalance(account, bech32Addr);
      return total.toFixed(2);
    } else {
      // For Cosmos wallets, use existing query
      const collateralDenom = getCollateralDenom();
      if (!collateralDenom) {
        throw new Error("Collateral denom not configured for current network");
      }
      const balance = await queryBalance(
        account,
        denom || collateralDenom,
        nibiru.endptTm
      );
      
      if (balance) {
        return (balance.amount / 10 ** process.env.REACT_APP_USDC_DECIMALS).toFixed(2);
      }
      return "0.00";
    }
  } catch (error) {
    console.error("Failed to fetch balance:", error);
    return "0.00";
  }
};

/**
 * Get user's locked deposits - updated to support EVM
 * @param {string} account - User's wallet address
 * @param {boolean} isEvm - Whether this is an EVM wallet
 * @returns {Promise<Array>} - List of locked deposits
 */
export const getUserLockedDeposits = async (account, isEvm = false) => {
  try {
    if (!account) return [];

    const decimals = parseInt(process.env.REACT_APP_USDC_DECIMALS || "6", 10);

    // Locked deposits are tracked on-chain against the *bech32* depositor, and the
    // escrowed shares are minted to the vault itself — they never show up as an ERC20
    // balance. An EVM wallet therefore has to look them up under its derived bech32
    // address; skipping that (as this used to) makes locked deposits invisible in EVM.
    let bech32Address = account;
    if (isEvm) {
      try {
        const { evmToBech32Address } = await import("../../../evm/services/EvmContract");
        bech32Address = await evmToBech32Address(account);
      } catch (conversionError) {
        console.error("Failed to convert EVM to bech32 for locked deposits:", conversionError);
        return [];
      }
    }

    const message = {
      user_locked_deposits: { user: bech32Address }
    };

    const result = await queryContract(
      getVaultContractAddress(),
      message,
      nibiru.endptTm
    );

    // The contract returns UserLockedDepositsResponse { locked_deposits: [(id, deposit)] },
    // not a bare array.
    const lockedDeposits = Array.isArray(result)
      ? result
      : (result?.locked_deposits || []);

    if (!lockedDeposits.length) {
      return [];
    }

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return lockedDeposits.map(entry => {
      const [id, details] = entry;

      const lockDurationMs = Number(details.lock_duration) * 1000;
      // `at_timestamp` is a cosmwasm Timestamp: nanoseconds as a string.
      const depositDate = new Date(Number(details.at_timestamp) / 1e6);
      const unlockDate = new Date(depositDate.getTime() + lockDurationMs);

      const collateralAmount = (Number(details.assets_deposited) / Math.pow(10, decimals)).toFixed(2);
      const sharesAmount = (Number(details.shares) / Math.pow(10, decimals)).toFixed(2);

      return {
        depositId: id,
        vaultId: "usdc",
        vaultName: "USDC Vault",
        depositAmount: collateralAmount,
        vaultTokens: sharesAmount,
        vaultTokenName: "SLP-USDC",
        depositDate: formatDate(depositDate),
        unlockDate: formatDate(unlockDate),
        isLocked: new Date() < unlockDate,
        lockedUntil: unlockDate,
        lockDuration: details.lock_duration
      };
    });
  } catch (error) {
    console.error("Error fetching user locked deposits:", error);
    return [];
  }
};

/**
 * Get user's withdrawal requests - updated to support EVM
 * @param {string} account - User's wallet address 
 * @param {boolean} isEvm - Whether this is an EVM wallet
 * @returns {Promise<Array>} - List of withdrawal requests
 */
export const getUserWithdrawRequests = async (account, isEvm = false) => {
  try {
    if (!account) return [];
    
    let bech32Address = account;
    
    if (isEvm) {
      console.log("Converting EVM address to bech32 for withdrawal requests");
      try {
        // Import evmToBech32Address from EvmContract
        const { evmToBech32Address } = await import("../../../evm/services/EvmContract");
        bech32Address = await evmToBech32Address(account);
        console.log("Converted EVM address to bech32:", { evm: account, bech32: bech32Address });
      } catch (conversionError) {
        console.error("Failed to convert EVM to bech32 for withdrawal requests:", conversionError);
        // Return empty array if conversion fails
        console.log("Skipping withdrawal requests due to conversion failure");
        return [];
      }
    }
    
    // Use the same query logic for both wallet types, but with converted address for EVM
    const message = {
      user_withdraw_requests: { user: bech32Address }
    };
    
    const result = await queryContract(
      getVaultContractAddress(),
      message,
      nibiru.endptTm
    );
    
    if (!result || !result.requests || !result.requests.length) {
      console.log("No withdrawal requests found for user:", bech32Address);
      return [];
    }
    
    // Get current epoch information
    const currentEpochMsg = { current_epoch: {} };
    const currentEpoch = await queryContract(
      getVaultContractAddress(),
      currentEpochMsg,
      nibiru.endptTm
    );
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    
    // Format withdrawal requests
    return result.requests.map(request => {
      const unlockEpoch = request.unlock_epoch;
      const canWithdraw = currentEpoch >= unlockEpoch;
      
      // Calculate estimated unlock date (72 hours per epoch in mainnet)
      const epochsToWait = Math.max(0, unlockEpoch - currentEpoch);
      const hoursToWait = epochsToWait * 72; // 72 hours per epoch in mainnet
      const unlockDate = new Date(Date.now() + (hoursToWait * 60 * 60 * 1000));
      
      return {
        shares: (parseFloat(request.shares) / Math.pow(10, process.env.REACT_APP_USDC_DECIMALS || 6)).toFixed(2),
        unlockEpoch: unlockEpoch,
        canWithdraw: canWithdraw,
        autoRedeem: request.auto_redeem || false,
        status: canWithdraw ? "Ready to Withdraw" : "Pending",
        estimatedUnlockDate: canWithdraw ? "Available Now" : formatDate(unlockDate),
        epochsRemaining: epochsToWait
      };
    });
  } catch (error) {
    console.error("Error fetching user withdrawal requests:", error);
    return [];
  }
};

/**
 * Deposit assets into vault
 * @param {string} account - User's wallet address
 * @param {string} amount - Amount to deposit
 * @param {object} dispatch - Redux dispatch function
 * @returns {Promise<boolean>} - Success or failure
 */
export const depositToVault = async (account, amount, dispatch) => {
  try {
    if (!account) {
      toast.error("Please connect your wallet");
      return false;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return false;
    }
    
    const maxDepositMsg = { max_deposit: {} };
    const maxDeposit = await queryContract(
      getVaultContractAddress(),
      maxDepositMsg,
      nibiru.endptTm
    );

    if (parseFloat(amount) > parseFloat(maxDeposit)) {
      toast.error("Deposit amount exceeds maximum allowed");
      return false;
    }
    
    // Convert to the proper denomination with decimals
    const depositAmount = Math.floor(parseFloat(amount) * (10 ** process.env.REACT_APP_USDC_DECIMALS)).toString();
    
    const message = {
      deposit: {
        receiver: null // Default to sender
      }
    };
    
    const collateralDenom = getCollateralDenom();
    if (!collateralDenom) {
      throw new Error("Collateral denom not configured for current network");
    }
    
    const tokenToSend = [
      {
        denom: collateralDenom,
        amount: depositAmount
      }
    ];
    
    const result = await executePerps(
      nibiru.chainId,
      nibiru.endptTm,
      getVaultContractAddress(),
      dispatch,
      message,
      account,
      "keplr",
      tokenToSend
    );
    
    if (result) {
      // toast.success("Deposit successful!");
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Deposit error:", error);
    toast.error(`Failed to deposit: ${error.message}`);
    return false;
  }
};

/**
 * Deposit with discount and lock for specified duration
 * @param {string} account - User's wallet address
 * @param {string} amount - Amount to deposit
 * @param {number} lockDuration - Lock duration in seconds
 * @param {object} dispatch - Redux dispatch function
 * @returns {Promise<boolean>} - Success or failure
 */
export const depositWithDiscountAndLock = async (account, amount, lockDuration, dispatch) => {
  try {
    if (!account) {
      toast.error("Please connect your wallet");
      return false;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return false;
    }

    const minLockDuration = await getMinLockDuration();
    const MAX_LOCK_DURATION = 31536000;

    if (lockDuration < minLockDuration) {
      toast.error(`Lock duration must be at least ${minLockDuration / (24 * 60 * 60)} days`);
      return false;
    }

    if (lockDuration > MAX_LOCK_DURATION) {
      toast.error("Lock duration cannot exceed 1 year");
      return false;
    }

    const depositAmount = Math.floor(parseFloat(amount) * (10 ** process.env.REACT_APP_USDC_DECIMALS)).toString();
    
    const message = {
      deposit: {
        lock_duration: lockDuration
      }
    };
    
    const collateralDenom = getCollateralDenom();
    if (!collateralDenom) {
      throw new Error("Collateral denom not configured for current network");
    }
    
    const tokenToSend = [
      {
        denom: collateralDenom,
        amount: depositAmount
      }
    ];
    
    const result = await executePerps(
      nibiru.chainId,
      nibiru.endptTm,
      getVaultContractAddress(),
      dispatch,
      message,
      account,
      "keplr",
      tokenToSend
    );
    
    if (result) {
      // toast.success("Locked deposit successful!");
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Locked deposit error:", error);
    toast.error(`Failed to lock deposit: ${error.message}`);
    return false;
  }
};

/**
 * Add this function to check if withdrawals are allowed
 */
export const checkWithdrawalAllowed = async () => {
  try {
    // Get current epoch info
    const currentEpochMsg = { current_epoch: {} };
    const currentEpoch = await queryContract(
      getVaultContractAddress(),
      currentEpochMsg,
      nibiru.endptTm
    );
    
    // Get current epoch start time
    const epochStartMsg = { get_current_epoch_start: {} };
    const epochStart = await queryContract(
      getVaultContractAddress(),
      epochStartMsg,
      nibiru.endptTm
    );
    
    // Get config for withdrawal window duration
    const configMsg = { config: {} };
    const config = await queryContract(
      getVaultContractAddress(),
      configMsg,
      nibiru.endptTm
    );

    const currentTime = Date.now();
    const epochStartTime = Number(epochStart) / 1000000; // Convert from nanoseconds
    const actionWindowDuration = 48 * 60 * 60 * 1000; // 48 hours in milliseconds (standard window)

    // Check if we're within the withdrawal window
    const isWithinWindow = currentTime >= epochStartTime && 
                          currentTime <= (epochStartTime + actionWindowDuration);

    if (!isWithinWindow) {
      // Calculate when the next epoch starts
      const epochDuration = 72 * 60 * 60 * 1000; // 72 hours in milliseconds
      const timeInCurrentEpoch = currentTime - epochStartTime;
      
      // Handle case where current epoch has been running longer than expected
      // Calculate how many full epochs have passed and when the next one starts
      const epochsPassed = Math.floor(timeInCurrentEpoch / epochDuration);
      const nextEpochStartTime = epochStartTime + ((epochsPassed + 1) * epochDuration);
      const nextEpochStart = new Date(nextEpochStartTime);
      
      return {
        allowed: false,
        message: "Withdrawals are only allowed during the first 48 hours of each epoch",
        nextWindow: nextEpochStart,
        currentEpoch: currentEpoch
      };
    }

    return { 
      allowed: true,
      currentEpoch: currentEpoch
    };
  } catch (error) {
    console.error("Error checking withdrawal allowance:", error);
    return {
      allowed: false,
      message: "Failed to check withdrawal status",
      error: error.message
    };
  }
};

/**
 * Update makeWithdrawRequest to include the check
 */
export const makeWithdrawRequest = async (account, dispatch, isEvm = false) => {
  try {
    if (!account) {
      toast.error("Please connect your wallet");
      return false;
    }

    // Check if withdrawals are allowed first
    const withdrawalStatus = await checkWithdrawalAllowed();
    if (!withdrawalStatus.allowed) {
      const nextWindow = withdrawalStatus.nextWindow 
        ? `\nNext window opens at ${withdrawalStatus.nextWindow.toLocaleString()}`
        : '';
      toast.error(`${withdrawalStatus.message}${nextWindow}`);
      return false;
    }

    if (isEvm) {
      // EVM withdrawal request.
      //
      // `withdrawAmount` is the bank-held share amount handed to the vault as funds;
      // `useErc20Amount` is what SaiEvm first converts out of the share ERC20 into
      // bank. Shares an EVM user holds sit in the ERC20, so they belong in
      // useErc20Amount — and the balance has to be read from the *share ERC20*
      // address, not the vault's bech32 address.
      const vaultAddress = getVaultContractAddress();
      const { getVaultShareErc20Address, getVaultShareBalanceBaseUnitsEvm } =
        await import("../../../evm/services/vaultsEvmService");

      let erc20Base = "0";
      try {
        const shareErc20 = await getVaultShareErc20Address(vaultAddress);
        erc20Base = await getVaultShareBalanceBaseUnitsEvm(account, shareErc20);
      } catch (erc20Error) {
        console.warn("Could not read vault share ERC20 balance:", erc20Error?.message);
      }

      // Shares that were never bridged stay in bank under the derived bech32 address.
      let bankBase = "0";
      try {
        const { evmToBech32Address } = await import("../../../evm/services/EvmContract");
        const bech32Address = await evmToBech32Address(account);
        const shareDenom = String(
          await queryContract(vaultAddress, { get_vault_share_denom: {} }, nibiru.endptTm)
        ).replace(/"/g, '');
        const bankBalance = await queryBalance(bech32Address, shareDenom, nibiru.endptTm);
        bankBase = bankBalance?.amount || "0";
      } catch (bankError) {
        console.warn("Could not read vault share bank balance:", bankError?.message);
      }

      if (BigInt(erc20Base) + BigInt(bankBase) <= 0n) {
        toast.error("You don't have any vault tokens to withdraw");
        return false;
      }

      const result = await makeWithdrawRequestEvm({
        vaultAddress: vaultAddress,
        withdrawAmount: bankBase,
        useErc20Amount: erc20Base
      });

      if (result.success) {
        // toast.success("Withdrawal request submitted successfully!");
        return true;
      }
      return false;
    } else if (!isEvm) {
      const vaultSharesBalance = await queryBalance(
        account,
        process.env.REACT_APP_VAULT_TOKEN_DENOM,
        nibiru.endptTm
      );
      
      if (!vaultSharesBalance || vaultSharesBalance.amount === "0") {
        toast.error("You don't have any vault tokens to withdraw");
        return false;
      }

    const message = {
      make_withdraw_request: {}
    };

    const tokenToSend = [
      {
        denom: process.env.REACT_APP_VAULT_TOKEN_DENOM,
        amount: vaultSharesBalance.amount
      }
    ];

    const result = await executePerps(
      nibiru.chainId,
      nibiru.endptTm,
      getVaultContractAddress(),
      dispatch,
      message,
      account,
      "keplr",
      tokenToSend
    );
    
    if (result) {
        // toast.success("Withdrawal request submitted successfully!");
      return true;
      }
    } else {
      toast.error("Please connect a supported wallet");
      return false;
    }
    
    return false;
  } catch (error) {
    console.error("Withdrawal request error:", error);

    // Handle specific error messages
    if (error.message?.includes("unlocking not allowed")) {
      toast.error("Withdrawals are currently not allowed. Please try again during the withdrawal window.");
    } else {
      toast.error(`Failed to submit withdrawal request: ${error.message}`);
    }
    return false;
  }
};

/**
 * Cancel a withdrawal request
 * @param {string} account - User's wallet address
 * @param {number} unlockEpoch - Epoch when shares would be unlocked
 * @param {object} dispatch - Redux dispatch function
 * @returns {Promise<boolean>} - Success or failure
 */
export const cancelWithdrawRequest = async (account, unlockEpoch, dispatch) => {
  try {
    if (!account) {
      toast.error("Please connect your wallet");
      return false;
    }
    
    const message = {
      cancel_withdraw_request: {
        unlock_epoch: unlockEpoch
      }
    };
    
    const result = await executePerps(
      nibiru.chainId,
      nibiru.endptTm,
      getVaultContractAddress(),
      dispatch,
      message,
      account,
      "keplr"
    );
    
    if (result) {
      toast.success("Withdrawal request cancelled successfully!");
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Cancel withdrawal request error:", error);
    toast.error(`Failed to cancel withdrawal request: ${error.message}`);
    return false;
  }
};

/**
 * Redeem vault shares for assets
 * @param {string} account - User's wallet address
 * @param {string} shares - Amount of shares to redeem
 * @param {object} dispatch - Redux dispatch function
 * @returns {Promise<boolean>} - Success or failure
 */
export const redeemShares = async (account, shares, dispatch) => {
  try {
    if (!account) {
      toast.error("Please connect your wallet");
      return false;
    }

    const withdrawRequests = await getUserWithdrawRequests(account, false);
    const readyRequests = withdrawRequests.filter(req => req.canWithdraw);

    if (readyRequests.length > 0) {
      console.log("Found ready withdrawal requests:", readyRequests);
      
      const totalSharesFromRequests = readyRequests.reduce((sum, req) => {
        const shareAmount = Math.floor(parseFloat(req.shares) * (10 ** process.env.REACT_APP_USDC_DECIMALS));
        return sum + shareAmount;
      }, 0);
      
      console.log("Total shares to redeem from withdrawal requests:", totalSharesFromRequests);
      
      const message = {
        redeem: {
          shares: totalSharesFromRequests.toString(),
          depositor: account
        }
      };
      
      const result = await executePerps(
        nibiru.chainId,
        nibiru.endptTm,
        getVaultContractAddress(),
        dispatch,
        message,
        account,
        "keplr"
      );
      
      if (result) {
        const totalSharesFormatted = (totalSharesFromRequests / (10 ** process.env.REACT_APP_USDC_DECIMALS)).toFixed(2);
        toast.success(`Redeemed ${totalSharesFormatted} shares from ${readyRequests.length} withdrawal request(s)!`);
        return true;
      }
    } else {
      if (!shares || parseFloat(shares) <= 0) {
        toast.error("Please enter a valid amount of shares or you have no ready withdrawal requests");
        return false;
      }
      
      const sharesAmount = Math.floor(parseFloat(shares) * (10 ** process.env.REACT_APP_USDC_DECIMALS)).toString();
      
      const maxRedeemMsg = { max_redeem: { depositor: account } };
      const maxRedeem = await queryContract(
        getVaultContractAddress(),
        maxRedeemMsg,
        nibiru.endptTm
      );
      
      if (maxRedeem && parseInt(sharesAmount) <= parseInt(maxRedeem)) {
        const message = {
          redeem: {
            shares: sharesAmount,
            depositor: null
          }
        };
        
        const result = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getVaultContractAddress(),
          dispatch,
          message,
          account,
          "keplr"
        );
        
        if (result) {
          toast.success("Shares redeemed immediately!");
          return true;
        }
      } else {
        // Need to make withdrawal request
        toast.info(`Immediate redemption not available (max: ${(maxRedeem / 10 ** process.env.REACT_APP_USDC_DECIMALS).toFixed(2)}). Making withdrawal request...`);
        
        const withdrawMessage = {
          make_withdraw_request: {
            auto_redeem: true
          }
        };
        
        const tokenToSend = [
          {
            denom: process.env.REACT_APP_VAULT_SHARE_DENOM,
            amount: sharesAmount
          }
        ];
        
        const result = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getVaultContractAddress(),
          dispatch,
          withdrawMessage,
          account,
          "keplr",
          tokenToSend
        );
        
        if (result) {
          toast.success("Withdrawal request submitted! You can redeem after the timelock period.");
          return true;
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error("Redeem shares error:", error);
    
    if (error.message.includes("zero redeem")) {
      toast.error("Cannot redeem zero shares. Please specify a valid amount.");
    } else if (error.message.includes("missing field `shares`")) {
      toast.error("Contract API error: shares field is required.");
    } else if (error.message.includes("withdraw request not found")) {
      toast.error("No withdrawal request found or timelock not expired.");
    } else if (error.message.includes("unknown field")) {
      toast.error("API mismatch error. The vault contract API has changed.");
    } else {
      toast.error(`Failed to redeem shares: ${error.message}`);
    }
    return false;
  }
};

/**
 * Unlock a previously locked deposit
 * @param {string} account - User's wallet address
 * @param {number} depositId - ID of locked deposit
 * @param {object} dispatch - Redux dispatch function
 * @returns {Promise<boolean>} - Success or failure
 */
export const unlockDeposit = async (account, depositId, dispatch) => {
  try {
    if (!account) {
      toast.error("Please connect your wallet");
      return false;
    }
    
    const message = {
      unlock_deposit: {
        deposit_id: depositId,
        receiver: null // Default to sender
      }
    };
    
    const result = await executePerps(
      nibiru.chainId,
      nibiru.endptTm,
      getVaultContractAddress(),
      dispatch,
      message,
      account,
      "keplr"
    );
    
    if (result) {
      toast.success("Deposit unlocked successfully!");
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Unlock deposit error:", error);
    toast.error(`Failed to unlock deposit: ${error.message}`);
    return false;
  }
};

/**
 * Get vault information
 * @returns {Promise<Object>} - Vault information
 */
export const getVaultInfo = async () => {
  try {
    // Get network-aware vault contract address
    const vaultContractAddress = getContractAddresses().vault;

    if (!vaultContractAddress) {
      throw new Error(`Vault contract address not configured for ${isTestnet() ? 'testnet' : 'mainnet'}`);
    }

    // Fire all 6 contract queries in parallel
    const [tvl, collateralDenom, vaultShareDenom, currentEpoch, marketCap, vaultSnapshot] = await Promise.all([
      queryContract(vaultContractAddress, { tvl: {} }, nibiru.endptTm),
      queryContract(vaultContractAddress, { get_collateral_denom: {} }, nibiru.endptTm),
      queryContract(vaultContractAddress, { get_vault_share_denom: {} }, nibiru.endptTm),
      queryContract(vaultContractAddress, { current_epoch: {} }, nibiru.endptTm),
      queryContract(vaultContractAddress, { market_cap: {} }, nibiru.endptTm),
      queryContract(vaultContractAddress, { vault_snapshot: {} }, nibiru.endptTm).catch(() => null),
    ]);

    const sharePrice = vaultSnapshot?.share_price ? parseFloat(vaultSnapshot.share_price) : 1.0;

    return {
      tvl: tvl || "0",
      collateralDenom: collateralDenom || "",
      vaultShareDenom: vaultShareDenom || "",
      currentEpoch: currentEpoch || 0,
      totalSupply: marketCap || "0",
      sharePrice,
    };
  } catch (error) {
    console.error("Error fetching vault info:", error);
    return {
      tvl: "0",
      collateralDenom: "",
      vaultShareDenom: "",
      currentEpoch: 0,
      totalSupply: "0",
      sharePrice: 1.0,
    };
  }
};

/**
 * Add this function to validate against max deposit
 */
export const getMaxDeposit = async () => {
  try {
    const message = { max_deposit: {} };
    const result = await queryContract(
      getVaultContractAddress(),
      message,
      nibiru.endptTm
    );
    return Number(result) / Math.pow(10, process.env.REACT_APP_USDC_DECIMALS);
  } catch (error) {
    console.error("Error fetching max deposit:", error);
    return Infinity; // Fallback
  }
};

/**
 * Calculate lock discount based on lock duration
 * @param {number} lockDuration - Duration in seconds
 * @returns {Promise<string>} - Discount as decimal (e.g. "0.05" for 5%)
 */
export const getLockDiscount = async (lockDuration) => {
  try {
    // Using the correct query name from the error message
    const collatMsg = { collateralization_p: {} };  // Fixed typo here
    
    const collatP = await queryContract(
      getVaultContractAddress(),
      collatMsg,
      nibiru.endptTm
    );
    
    // Get lock discount
    const lockDiscountMsg = { 
      lock_discount_p: {
        collat_p: collatP || "1", // Default to 100% if not available
        lock_duration: lockDuration
      }
    };
    
    const discount = await queryContract(
      getVaultContractAddress(),
      lockDiscountMsg,
      nibiru.endptTm
    );
    
    // Convert from decimal to percentage string
    return parseFloat(discount || 0).toFixed(4);
  } catch (error) {
    console.error("Error calculating lock discount:", error);
    return "0";
  }
};

/**
 * Get withdrawal epochs timelock
 * @returns {Promise<number>} - Number of epochs
 */
export const getWithdrawEpochsTimelock = async () => {
  try {
    const message = { withdraw_epochs_timelock: {} };
    
    const result = await queryContract(
      getVaultContractAddress(),
      message,
      nibiru.endptTm
    );
    
    return parseInt(result || 0);
  } catch (error) {
    console.error("Error fetching withdraw epochs timelock:", error);
    return 1; // Default to 1 epoch
  }
};

/**
 * Calculate estimated shares based on deposit amount
 * @param {number} depositAmount - Amount to deposit
 * @returns {Promise<number>} - Estimated shares
 */
export const getEstimatedShares = async (depositAmount) => {
  const tvl = await queryContract(
    getVaultContractAddress(),
    { tvl: {} },
    nibiru.endptTm
  );
  
  const totalSupply = await queryContract(
    getVaultContractAddress(),
    { market_cap: {} },
    nibiru.endptTm
  );
  
  // Calculate share price: TVL / Total Supply
  const sharePrice = Number(tvl) / Number(totalSupply);
  
  // Estimated shares = deposit amount / share price
  return depositAmount / sharePrice;
};

/**
 * Add this function to get minimum lock duration
 */
export const getMinLockDuration = async () => {
  try {
    const MIN_LOCK_DURATION = 30 * 24 * 60 * 60;
    
    console.log("Using default minimum lock duration:", MIN_LOCK_DURATION);
    return MIN_LOCK_DURATION;
  } catch (error) {
    console.error("Error handling minimum lock duration:", error);
    return 30 * 24 * 60 * 60;
  }
};

/**
 * Add this function
 */
export const getCollateralizationP = async () => {
  try {
    const message = { collateralization_p: {} };
    const result = await queryContract(
      getVaultContractAddress(),
      message,
      nibiru.endptTm
    );
    // Convert from decimal to percentage for display
    return (Number(result) * 100).toFixed(2);
  } catch (error) {
    console.error("Error fetching collateralization percentage:", error);
    return "100.00"; // Fallback
  }
};

// Helper function to format dates consistently
const formatDate = (date) => {
  try {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      console.warn("Invalid date in formatDate:", date);
      return "N/A";
    }
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error("Error formatting date:", error, date);
    return "N/A";
  }
};

/**
 * Format APY value with comprehensive validation and edge case handling
 * @param {any} apyValue - APY value to format (can be number, string, null, undefined)
 * @returns {string} Formatted APY string (e.g., "6.25%", "1000%+", "0.00%")
 */
function formatApy(apyValue) {
  // 1. Empty / null / undefined
  if (apyValue === null || apyValue === undefined || apyValue === "") {
    return "0.00%";
  }

  const apyPercent = Number(apyValue);

  // 2. NaN → show 0%
  if (Number.isNaN(apyPercent)) {
    return "0.00%";
  }

  // 3. Infinity / -Infinity → cap
  if (!Number.isFinite(apyPercent)) {
    return "1000%+";
  }

  // 4. Unrealistic explosion guard
  if (Math.abs(apyPercent) > 1000) {
    return apyPercent > 0 ? "1000%+" : "-1000%+";
  }

  // 5. JS negative zero fix
  const safeApy = Object.is(apyPercent, -0) ? 0 : apyPercent;

  // 6. Normal case
  return `${safeApy.toFixed(2)}%`;
}

/**
 * Fetch vault APY from GraphQL API
 * @param {string} vaultAddress - Vault contract address (optional, will use configured address if not provided)
 * @returns {Promise<string>} APY as formatted string (e.g., "6.25%")
 */
export const getVaultApy = async (vaultAddress = null) => {
  try {
    // Use provided address or get from config
    const vaultAddr = vaultAddress || getContractAddresses().vault;
    
    if (!vaultAddr) {
      console.warn("Vault address not available, using fallback APY");
      return "6.25%"; // Fallback
    }
    
    // Determine GraphQL endpoint based on network
    const graphqlUrl = isTestnet() 
      ? "https://sai-keeper.testnet-2.nibiru.fi/query"
      : "https://sai-keeper.nibiru.fi/query";
    
    console.log("Fetching vault APY from GraphQL:", { vaultAddr, graphqlUrl });
    
    const response = await fetch(graphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({
        query: `
          query VaultsApy($vault: String) {
            lp {
              vaults(where: { address: $vault }) {
                address
                apy
                sharePrice
                tvl
              }
            }
          }
        `,
        variables: { vault: vaultAddr },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    console.log("GraphQL vault APY response:", json);
    
    if (json.errors) {
      console.error("GraphQL errors:", json.errors);
      throw new Error("Failed to fetch vault APY from GraphQL");
    }
    
    if (!json.data || !json.data.lp || !json.data.lp.vaults || json.data.lp.vaults.length === 0) {
      console.warn("No vault data found in GraphQL response, using fallback APY");
      return "6.25%"; // Fallback
    }
    
    const vaultData = json.data.lp.vaults[0];
    const apyValue = vaultData.apy;
    
    // Format APY using comprehensive formatter
    // API returns APY as percentage (e.g., 6.25 for 6.25%)
    const formattedApy = formatApy(apyValue);
    console.log("Vault APY fetched successfully:", formattedApy);
    
    return formattedApy;
  } catch (error) {
    console.error(`Error fetching vault APY for vault ${vaultAddress || 'default'}:`, error);
    // Return fallback APY on error
    return "6.25%";
  }
};

/**
 * Calculate 30-day APY from vault share price time series
 * @param {string} vaultAddress - Vault contract address (optional, will use configured address if not provided)
 * @returns {Promise<string>} 30D APY as formatted string (e.g., "6.25%")
 */
export const getVault30DayApy = async (vaultAddress = null) => {
  try {
    // Use provided address or get from config (handles mainnet/testnet automatically)
    const vaultAddr = vaultAddress || getContractAddresses().vault;
    
    if (!vaultAddr) {
      console.warn("Vault address not available, using fallback 30D APY");
      return "0.00%"; // Fallback
    }
    
    // Determine GraphQL endpoint based on network
    const graphqlUrl = isTestnet() 
      ? "https://sai-keeper.testnet-2.nibiru.fi/query"
      : "https://sai-keeper.nibiru.fi/query";
    
    const networkType = isTestnet() ? "testnet" : "mainnet";
    console.log("Fetching vault 30D APY from GraphQL:", { 
      vaultAddr, 
      graphqlUrl, 
      networkType,
      vaultAddressProvided: !!vaultAddress 
    });
    
    const response = await fetch(graphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({
        query: `
          query VaultApySeries($vault: String!) {
            lp {
              vaultStats(
                vault: $vault
                range: "30d"
              ) {
                timestamp
                sharePrice
              }
            }
          }
        `,
        variables: { vault: vaultAddr },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    console.log("GraphQL vault 30D APY response:", json);
    
    if (json.errors) {
      console.error("GraphQL errors:", json.errors);
      throw new Error("Failed to fetch vault 30D APY from GraphQL");
    }
    
    if (!json.data || !json.data.lp || !json.data.lp.vaultStats || !Array.isArray(json.data.lp.vaultStats)) {
      console.warn("No vault stats data found in GraphQL response, using fallback 30D APY");
      return "0.00%"; // Fallback
    }
    
    const vaultStats = json.data.lp.vaultStats;
    
    // Edge case: fewer than 2 datapoints
    if (vaultStats.length < 2) {
      console.warn("Insufficient datapoints for 30D APY calculation (< 2), returning 0.00%");
      return "0.00%";
    }
    
    // Sort by timestamp ascending
    const sortedStats = [...vaultStats].sort((a, b) => {
      const timestampA = parseInt(a.timestamp) || 0;
      const timestampB = parseInt(b.timestamp) || 0;
      return timestampA - timestampB;
    });
    
    // Get start (first) and end (last) datapoints
    const start = sortedStats[0];
    const end = sortedStats[sortedStats.length - 1];
    
    const sharePriceStart = parseFloat(start.sharePrice);
    const sharePriceEnd = parseFloat(end.sharePrice);
    const timestampStart = parseInt(start.timestamp) || 0;
    const timestampEnd = parseInt(end.timestamp) || 0;
    
    // Validate share prices
    if (isNaN(sharePriceStart) || isNaN(sharePriceEnd) || sharePriceStart <= 0 || sharePriceEnd <= 0) {
      console.warn("Invalid share price values, returning 0.00%");
      return "0.00%";
    }
    
    // Edge case: sharePrice_start === sharePrice_end
    if (sharePriceStart === sharePriceEnd) {
      console.warn("Share prices are equal, returning 0.00%");
      return "0.00%";
    }
    
    // Calculate actual days from timestamp delta
    // Timestamps are typically in seconds or milliseconds
    // Convert to days: (end - start) / (1000 * 60 * 60 * 24) for milliseconds
    // or (end - start) / (60 * 60 * 24) for seconds
    const timestampDelta = timestampEnd - timestampStart;
    // Assume timestamps are in seconds (common for GraphQL APIs)
    // If they're in milliseconds, the delta will be much larger and we'll detect it
    let days = timestampDelta > 86400000 
      ? timestampDelta / (1000 * 60 * 60 * 24) // milliseconds
      : timestampDelta / (60 * 60 * 24); // seconds
    
    // Validate days calculation
    if (isNaN(days) || days <= 0 || days > 365) {
      console.warn(`Invalid days calculation (${days}), using fallback 30 days`);
      days = 30;
    }
    
    // Calculate return using end/start (NOT start/end): 
    // return_30d = (sharePrice_end / sharePrice_start) - 1
    const return30d = (sharePriceEnd / sharePriceStart) - 1;
    
    // Annualize using actual days from timestamp delta:
    // apy_30d = (1 + return_30d) ** (365 / days) - 1
    const apy30d = Math.pow(1 + return30d, 365 / days) - 1;
    
    // Convert to percentage and format using comprehensive formatter
    const apy30dPercent = apy30d * 100;
    const formattedApy = formatApy(apy30dPercent);
    
    console.log("Vault 30D APY calculated successfully:", {
      sharePriceStart,
      sharePriceEnd,
      timestampStart,
      timestampEnd,
      days: days,
      return30d,
      apy30d,
      apy30dPercent,
      formattedApy
    });
    
    return formattedApy;
  } catch (error) {
    console.error(`Error calculating vault 30D APY for vault ${vaultAddress || 'default'}:`, error);
    // Return fallback 30D APY on error
    return "0.00%";
  }
};

/**
 * Fetch the vault performance time series for charting.
 * @param {string} range - "7D" | "30D" | "ALL" (keeper accepts 7d/30d/all, case-insensitive)
 * @param {string} vaultAddress - optional; defaults to configured vault
 * @returns {Promise<Array>} rows: { timestamp, sharePrice, depositsUsd, withdrawalsUsd, volumeUsd, apy }
 */
export const getVaultStatsSeries = async (range = "7D", vaultAddress = null) => {
  try {
    const vaultAddr = vaultAddress || getContractAddresses().vault;
    if (!vaultAddr) return [];

    const graphqlUrl = isTestnet()
      ? "https://sai-keeper.testnet-2.nibiru.fi/query"
      : "https://sai-keeper.nibiru.fi/query";

    const rangeArg = String(range).toLowerCase(); // "7d" | "30d" | "all"

    const response = await fetch(graphqlUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        query: `
          query VaultStatsSeries($vault: String!, $range: String!) {
            lp {
              vaultStats(vault: $vault, range: $range) {
                timestamp
                sharePrice
                depositsUsd
                withdrawalsUsd
                volumeUsd
                apy
              }
            }
          }
        `,
        variables: { vault: vaultAddr, range: rangeArg },
      }),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    if (json.errors) throw new Error(JSON.stringify(json.errors));

    const rows = json?.data?.lp?.vaultStats || [];
    // Ascending by time, normalized to numbers.
    return [...rows]
      .map((r) => ({
        timestamp: r.timestamp,
        ts: new Date(r.timestamp).getTime(),
        sharePrice: Number(r.sharePrice) || 0,
        depositsUsd: Number(r.depositsUsd) || 0,
        withdrawalsUsd: Number(r.withdrawalsUsd) || 0,
        volumeUsd: Number(r.volumeUsd) || 0,
        apy: Number(r.apy) || 0,
      }))
      .sort((a, b) => a.ts - b.ts);
  } catch (error) {
    console.error("Error fetching vault stats series:", error);
    return [];
  }
};

/**
 * Get USDC collateral price from Oracle contract
 * @returns {Promise<number|null>} USDC price in USD
 */
export const getCollateralUSDCPrice = async () => {
  try {
    console.log("Fetching USDC collateral price from oracle...");
    
    // Auto-detect USDC token index based on network
    // Testnet uses token index 3, Mainnet uses token index 1
    let usdcTokenIndex;
    
    if (isTestnet()) {
      usdcTokenIndex = 3; // Testnet USDC token index
    } else {
      usdcTokenIndex = 1; // Mainnet USDC token index
    }
    
    // Allow environment variable to override (for flexibility)
    const usdcEnvVar = process.env.REACT_APP_PERP_TOKEN_INDEXES_USDC;
    if (usdcEnvVar) {
      const match = usdcEnvVar.match(/TokenIndex\((\d+)\)/);
      if (match) {
        usdcTokenIndex = parseInt(match[1], 10);
      }
    }
    
    console.log(`Using USDC token index: ${usdcTokenIndex} (${isTestnet() ? 'testnet' : 'mainnet'})`);
    
    const message = {
      get_price: {
        index: usdcTokenIndex
      }
    };

    // Get network-aware oracle contract address
    const oracleContractAddress = getContractAddresses().oracle;
    if (!oracleContractAddress) {
      throw new Error(`Oracle contract address not configured for ${isTestnet() ? 'testnet' : 'mainnet'}`);
    }

    console.log(`Querying oracle contract: ${oracleContractAddress} for token index: ${usdcTokenIndex}`);

    const result = await queryContract(
      oracleContractAddress,
      message,
      nibiru.endptTm
    );

    console.log("Oracle USDC price result:", result);

    if (result && result.price) {
      const usdcPrice = parseFloat(result.price);
      console.log("USDC collateral price:", usdcPrice);
      return usdcPrice;
    } else if (result && typeof result === 'string') {
      const usdcPrice = parseFloat(result);
      console.log("USDC collateral price (parsed from string):", usdcPrice);
      return usdcPrice;
    } else if (result && typeof result === 'number') {
      console.log("USDC collateral price (direct number):", result);
      return result;
    }

    console.warn("Unexpected oracle result format:", result);
    return null;
  } catch (error) {
    console.error("Failed to get USDC collateral price from oracle:", error);
    return null;
  }
};

const vaultQueries = {
  getUserVaultDeposits,
  getUserLockedDeposits,
  getUserWithdrawRequests,
  getUserTokenBalance,
  depositToVault,
  depositWithDiscountAndLock,
  makeWithdrawRequest,
  cancelWithdrawRequest,
  redeemShares,
  unlockDeposit,
  getVaultInfo,
  getMaxDeposit,
  getLockDiscount,
  getWithdrawEpochsTimelock,
  getEstimatedShares,
  getMinLockDuration,
  getCollateralizationP,
  getCollateralUSDCPrice,
  getVaultApy,
  getVault30DayApy,
  getVaultStatsSeries
};

export default vaultQueries;